/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import DatePickerOutline from 'components/MDDatePicker/DatePickerOutline';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import FormQuantityAdd from './FormQuantityAdd';

const initFind = {
  search: '',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  checkpoint: 'ALL',
  best: 'ALL',
  branch: 'ALL',
  condition: 'branch',
  sort: 'diffbranch',
};

function SearchPoint({ add }) {
  const mod = 'export_request_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const txtSearch = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/viewpoint`, app.token, {
      lang: app.lang,
      ref_id: id,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      best: data.best,
      condition: data.condition,
      sort: data.sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const addCallback = (item, quantity) => {
    add({
      export_id: id,
      list_id: 0,
      invite_id: item.ref_id,
      barcode_id: item.barcode_id,
      quantity: quantity,
      quantity_balance: quantity,
      unit_id: item.unit_id,
    });
  };

  const open = () => {
    setModalAdd(true);
    view(find);

    setTimeout(function () {
      txtSearch.current.focus();
    }, 500);
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const btnInvoite = (row) => {
    if (row.stock_invite * 1 <= 0) return '-';

    return (
      <MDBox>
        <MDBox>
          <Tooltip title={`เติม ${Func.numberDisplay(row.stock_invite)} ${row.unit_name}`}>
            <MDButton
              variant='text'
              color='error'
              size='small'
              sx={{
                p: 0,
                m: 0,
                fontSize: '1rem',
                display: 'inline-block',
                textAlign: 'right',
                minHeight: 'unset',
                fontWeight: 'normal',
              }}
              onClick={() => addCallback(row, row.stock_invite)}
            >
              <Icon>add_circle_outline</Icon> {Func.numberDisplay(row.stock_invite)}
            </MDButton>
          </Tooltip>
        </MDBox>
        <MDBox>{row.unit_name}</MDBox>
      </MDBox>
    );
  };

  const btnAdd = (row) => {
    if (row.export_all * 1 <= 0) return '-';

    return (
      <MDBox>
        <MDBox>
          <Tooltip title={`เติม ${Func.numberDisplay(row.export_all)} ${row.unit_name}`}>
            <MDButton
              variant='text'
              color='info'
              size='small'
              sx={{
                p: 0,
                m: 0,
                fontSize: '1rem',
                display: 'inline-block',
                textAlign: 'right',
                minHeight: 'unset',
                fontWeight: 'normal',
              }}
              onClick={() => addCallback(row, row.export_all)}
            >
              <Icon>add_circle_outline</Icon> {Func.numberDisplay(row.export_all)}
            </MDButton>
          </Tooltip>
        </MDBox>
        <MDBox>{row.unit_name}</MDBox>
      </MDBox>
    );
  };

  const btnAddSto = (row) => {
    if (row.export_balance * 1 <= 0) return '-';

    return (
      <MDBox>
        <MDBox>
          <Tooltip title={`เติม ${Func.numberDisplay(row.export_balance)} ${row.unit_name}`}>
            <MDButton
              variant='text'
              color='success'
              size='small'
              sx={{
                p: 0,
                m: 0,
                fontSize: '1rem',
                display: 'inline-block',
                textAlign: 'right',
                minHeight: 'unset',
                fontWeight: 'normal',
              }}
              onClick={() => addCallback(row, row.export_balance)}
            >
              <Icon>add_circle_outline</Icon> {Func.numberDisplay(row.export_balance)}
            </MDButton>
          </Tooltip>
        </MDBox>
        <MDBox>{row.unit_name}</MDBox>
      </MDBox>
    );
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.best}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, best: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>Rating :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='1'>A</MenuItem>
              <MenuItem value='2'>B</MenuItem>
              <MenuItem value='3'>C</MenuItem>
              <MenuItem value='4'>D</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.condition}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, condition: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>เงื่อนไข :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='branch'>หน้าร้านไม่เพียงพอ</MenuItem>
              <MenuItem value='invite'>ค้างส่ง</MenuItem>
              <MenuItem value='branch_invite'>หน้าร้านไม่เพียงพอ ค้างส่ง</MenuItem>
            </MDInput>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const diffStockDisplay = (row) => {
    const diff = row.diff_stock * 1;

    if (diff <= 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.purchase_stock)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(row.purchase_stock)}</MDBox>;
  };

  const diffBranchDisplay = (row) => {
    const diff = row.diff_branch * 1;

    if (diff <= 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.purchase_branch)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(row.purchase_branch)}</MDBox>;
  };

  const quantityDisplay = (inp) => {
    if (inp * 1 < 0) {
      return <MDBox color='error'>{Func.numberDisplay(inp)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(inp)}</MDBox>;
  };

  const inviteDisplay = (row) => {
    if (row.balance_invite * 1 > 0) {
      return <MDBox color='error'>{Func.numberDisplay(row.balance_invite)}</MDBox>;
    }

    return <MDBox>{Func.numberDisplay(row.balance_invite)}</MDBox>;
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ minHeight: 550, boxShadow: 0 }}>
              <Table aria-label='datatable table'>
                <TableHead>
                  <TableRow>
                    <TableCell className='nowrap' align='center' rowSpan={2}>
                      No.
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      รูป
                    </TableCell>
                    <TableCell className='nowrap' rowSpan={2}>
                      สินค้า
                    </TableCell>
                    <TableCell className='nowrap' align='center' rowSpan={2}>
                      Rating
                    </TableCell>
                    <TableCell className='nowrap' align='center' rowSpan={2}>
                      Min / Max
                    </TableCell>
                    <TableCell className='nowrap' align='right' rowSpan={2}>
                      ขายเฉลี่ย
                      <br />
                      ต่อวัน
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='center' colSpan={3}>
                      หน้าร้าน
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='center' colSpan={2}>
                      STO
                    </TableCell>
                    <TableCell className='nowrap highlight-danger' align='center' colSpan={4}>
                      ขอโอน รอบ 7 วัน
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='center' colSpan={3}>
                      โอนย้าย
                    </TableCell>
                    <TableCell align='center' className='nowrap' width={130} rowSpan={2}>
                      จำนวนโอน
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      จุดเติม
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      คลัง
                    </TableCell>
                    <TableCell className='nowrap highlight-warning' align='right'>
                      คงเหลือ
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      คลัง
                    </TableCell>
                    <TableCell className='nowrap highlight-info' align='right'>
                      พร้อมโอน
                    </TableCell>
                    <TableCell className='nowrap highlight-danger' align='right'>
                      จำนวน
                    </TableCell>
                    <TableCell className='nowrap highlight-danger' align='right'>
                      ขอ
                    </TableCell>
                    <TableCell className='nowrap highlight-danger' align='right'>
                      ค้างส่ง
                    </TableCell>
                    <TableCell className='nowrap highlight-danger' align='right'>
                      ให้ได้
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      ให้ได้
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      หักร้าน
                    </TableCell>
                    <TableCell className='nowrap highlight-success' align='right'>
                      โอนแล้ว
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.no}</TableCell>
                      <TableCell width='40'>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' sx={{ width: 100 }}>
                          {row.product_name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        {row.best_name === '' ? (
                          'N/A'
                        ) : (
                          <MDBox>
                            <MDBox>
                              <code>{row.best_name}</code>
                            </MDBox>
                            <MDBox>{Func.numberDisplay(row.percent_add)}%</MDBox>
                          </MDBox>
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap'>
                          {Func.numberDisplay(row.quantity_min)} /{' '}
                          {Func.numberDisplay(row.quantity_max)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_avg)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox color='info'>{Func.numberDisplay(row.delivery_point)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{quantityDisplay(row.quantity_balance)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-warning'>
                        <MDBox>{diffBranchDisplay(row)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_stock)}</MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-info'>
                        <MDBox>{diffStockDisplay(row)}</MDBox>
                        <MDBox>วัน</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.count_invite)}</MDBox>
                        <MDBox>บิล</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_invite)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='highlight-danger'>
                        <MDBox>{inviteDisplay(row)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>{btnInvoite(row)}</TableCell>
                      <TableCell align='right'>{btnAdd(row)}</TableCell>
                      <TableCell align='right'>{btnAddSto(row)}</TableCell>
                      <TableCell align='right' className='highlight-success'>
                        <MDBox>{Func.numberDisplay(row.quantity_move)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='center' width={130}>
                        <FormQuantityAdd parent={row} callback={addCallback} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalAdd(false)}
      >
        <DialogTitle>
          จุดโอน
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      <MDButton variant='outlined' color='primary' onClick={open}>
        <Icon>add_circle_outline</Icon>&nbsp;จุดโอน
      </MDButton>

      {renderAdd()}
      {renderLoading()}
      {notify}
    </div>
  );
}

SearchPoint.propTypes = {
  add: PropTypes.func.isRequired,
};

export default SearchPoint;
